import { useState, useEffect, type ReactNode } from 'react';
import {
  doc, collection,   getDocs, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import { useSettings } from '../hooks/useSettings';
import { Button, Modal, ModalBanner, FormField } from './ui';
import { fmtMoney, getCurrencySymbol, findExchangeRate } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesApPayment } from '../utils/ledger';
import { dueGroupOutstanding, dueGroupStatus, aggregateFromDueGroups } from '../utils/dueGroups';
import type { PurchaseInvoice, PaymentStatus, BankAccount, DueGroup } from '../types';

function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
function round2(n: number) { return Math.round((n + Number.EPSILON) * 100) / 100; }

function todayISO() {
  return new Date().toISOString().split('T')[0];
}
// Lot No is stored in full, but shown here as just the prefix + last 4 digits
// to keep the header compact. Keeping the prefix matters — a bare last-4
// truncation collapses "PC-2026-0001" and "PL-2026-0001" into the same
// "0001", making certified and loose lots indistinguishable.
function lotNoDisplay(invoiceNo?: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}

interface PaymentForm {
  bankAccountId: string;
  amount:        string;
  // Conversion rate (1 base-currency unit = this many of the paying
  // account's own currency units) — only meaningful (and shown) when that
  // account's currency differs from the business's base currency. Drives
  // the converted bank-side amount (amount * rate); see needsConversion/
  // bankAmt below. Pre-filled from BusinessSettings.exchangeRates when a
  // matching pair is configured, always freely editable.
  rate:          string;
  paidDate:      string;
  referenceNo:   string;
  notes:         string;
}

const EMPTY: PaymentForm = {
  bankAccountId: '',
  amount: '', rate: '', paidDate: todayISO(),
  referenceNo: '', notes: '',
};

interface Props {
  invoice: PurchaseInvoice | null;
  onClose: () => void;
  /** Resolved supplier name for the header title — callers already have an entity lookup handy. */
  entityName?: string;
  /** Pre-select this DueGroup (e.g. the caller opened this modal from one
   *  specific due-date row in AP's flattened list) instead of defaulting
   *  to the earliest unpaid group. Ignored on an 'average'-mode invoice. */
  initialGroupId?: string;
  /** Starting values (Bank › New › Payment: the amount, account and date
   *  already known from the bank). Everything stays editable. */
  prefill?: { amount?: number; bankAccountId?: string; paidDate?: string };
  /** Called after a successful save with the amount recorded. */
  onSaved?: (amount: number) => void;
}

// The one identifier worth calling out from the rest — same highlighted pill
// used for Lot No everywhere else in the app.
function LotPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

// ── Mini icons ────────────────────────────────────────────────────────────────

function IconCash() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="1.5" y="5" width="17" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 10h1M14 10h1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconBank() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="8" width="16" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 8l8-5 8 5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5 11.5v3.5M10 11.5v3.5M15 11.5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7.5l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function RecordPaymentModal({ invoice, onClose, entityName, initialGroupId, prefill, onSaved }: Props) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const baseCurrency = settings.currency;
  const [form,     setForm]     = useState<PaymentForm>(EMPTY);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  // Once the user types directly into the Conversion Rate field, stop
  // re-overwriting it with the configured Dashboard rate — same "auto-fill
  // but never locks you out of the real figure" convention as Purchase's
  // Code auto-numbering / Discount%↔Price mutual derivation.
  const [rateTouched, setRateTouched] = useState(false);

  useEffect(() => {
    if (!invoice || !business) return;
    const db = getClientDb();
    if (!db) return;
    getDocs(
      collection(db, 'bankAccounts'),
    ).then(snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount));
      const accs = all
        .filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setAccounts(accs);
      const def = accs.find(a => a.id === prefill?.bankAccountId) ?? accs.find(a => a.isDefault) ?? accs[0];
      if (def) setForm(f => ({ ...f, bankAccountId: def.id }));
    }).catch(err => console.error('Failed to load bank accounts:', err));
  }, [invoice, business, prefill?.bankAccountId]);

  // Pre-filled amount / date for this invoice (see `prefill`).
  const [prefilledFor, setPrefilledFor] = useState('');
  if (!invoice && prefilledFor) setPrefilledFor('');
  if (invoice && prefill && prefilledFor !== invoice.id) {
    setPrefilledFor(invoice.id);
    setForm(f => ({
      ...f,
      amount: prefill.amount && prefill.amount > 0 ? String(Math.round(prefill.amount * 100) / 100) : f.amount,
      paidDate: prefill.paidDate || f.paidDate,
    }));
  }

  // A 'separate'-mode invoice's due groups — default-select the earliest
  // one that isn't fully paid yet (same "pick a sensible default" spirit
  // as the bank-account auto-select above), but the person recording the
  // payment can pick a different one.
  const groups     = invoice?.dueDateMode === 'separate' ? (invoice.dueGroups ?? []) : [];
  const isSeparate = groups.length > 0;
  useEffect(() => {
    if (!isSeparate) { setSelectedGroupId(''); return; }
    const requested = initialGroupId ? groups.find(g => g.id === initialGroupId) : undefined;
    const firstOpen = groups.find(g => dueGroupOutstanding(g) > 0.005) ?? groups[0];
    setSelectedGroupId((requested ?? firstOpen).id);
  }, [invoice?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // These don't depend on `invoice` at all, so — same reason `groups`/
  // `isSeparate` above are computed before the early return — they (and the
  // two effects that use them) must be too, or the hook count changes the
  // moment `invoice` flips from null to non-null (React's Rules of Hooks).
  const selectedAccount = accounts.find(a => a.id === form.bankAccountId) ?? null;
  const needsConversion = !!selectedAccount && selectedAccount.currency !== baseCurrency;
  // The configured rate, from Settings › the Dashboard's Exchange Rates
  // card — just the auto-fill SOURCE. form.rate (below) is what's actually
  // used everywhere once conversion is needed, since it's what the user can
  // see and correct if the bank's real rate differs today.
  const configuredRate = needsConversion ? findExchangeRate(settings.exchangeRates, baseCurrency, selectedAccount!.currency) : null;
  const payAmt          = parseFloat(form.amount) || 0;

  // Switching accounts means any previously-typed rate no longer applies
  // (different currency pair) — let auto-fill take over again for the
  // newly-selected account.
  useEffect(() => {
    setRateTouched(false);
  }, [form.bankAccountId]);

  // Auto-fill the Conversion Rate field from the configured Dashboard pair
  // whenever the account changes — but only until the user types into it
  // directly (rateTouched).
  useEffect(() => {
    if (!needsConversion || rateTouched || !configuredRate) return;
    setForm(f => ({ ...f, rate: String(configuredRate) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsConversion, rateTouched, configuredRate]);

  if (!invoice) return null;
  const inv = invoice; // narrowed, non-null copy — TS doesn't carry the `if` narrowing into nested closures like handleSave below

  const selectedGroup = isSeparate ? groups.find(g => g.id === selectedGroupId) ?? null : null;
  // The invoice's own figures (outstanding, due amount, etc.) are always in
  // the business's base currency — never the paying account's own currency,
  // which may differ (see needsConversion above).
  const currency = baseCurrency;
  const sym       = getCurrencySymbol(currency);
  const bankSym   = selectedAccount ? getCurrencySymbol(selectedAccount.currency) : '';

  // In separate mode every figure below is scoped to the SELECTED due
  // group, not the whole invoice — that's the whole point of "payment
  // tracking follows the due-date grouping instead of the invoice."
  const dueAmount    = isSeparate ? (selectedGroup?.amountUSD ?? 0) : invoice.totalAmountUSD;
  const alreadyPaid  = isSeparate ? (selectedGroup?.paidUSD ?? 0) : (invoice.paidUSD ?? 0);
  const credited     = isSeparate ? (selectedGroup?.creditedUSD ?? 0) : (invoice.creditedUSD ?? 0);   // returns + debit notes (Phase B1)
  const outstanding  = Math.max(0, dueAmount - alreadyPaid - credited);
  // The figure that actually moves in the paying account's own currency —
  // payAmt itself when no conversion is needed, otherwise payAmt × the
  // (auto-filled-but-editable) Conversion Rate.
  const rate           = needsConversion ? (parseFloat(form.rate) || 0) : 1;
  const bankAmt       = needsConversion ? payAmt * rate : payAmt;
  const remaining    = Math.max(0, outstanding - payAmt);
  const afterBalance = selectedAccount ? selectedAccount.currentBalance - bankAmt : null;
  const isOverpaying = payAmt > outstanding + 0.005;

  const previewStatus: PaymentStatus =
    payAmt >= outstanding - 0.005 && payAmt > 0 ? 'paid'
    : payAmt > 0 ? 'partial'
    : 'unpaid';

  function set<K extends keyof PaymentForm>(k: K, v: PaymentForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function validate(): string {
    if (isSeparate && !selectedGroup) return 'Select which due date you’re paying.';
    if (!form.bankAccountId) return 'Select an account to pay from.';
    const amt = parseFloat(form.amount);
    if (!form.amount || isNaN(amt) || amt <= 0) return 'Enter a valid amount.';
    if (amt > outstanding + 0.005) return `Payment amount exceeds the outstanding balance of ${fmtMoney(outstanding, currency)}. Please adjust the amount before proceeding.`;
    if (needsConversion) {
      const r = parseFloat(form.rate);
      if (!form.rate || isNaN(r) || r <= 0) {
        return `Enter the conversion rate to ${selectedAccount!.currency} — 1 ${baseCurrency} equals how many ${selectedAccount!.currency}.`;
      }
    }
    if (!form.paidDate) return 'Select payment date.';
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }

    const amt        = parseFloat(form.amount);
    const bankAmount = needsConversion ? amt * parseFloat(form.rate) : amt;
    const newPaid = alreadyPaid + amt;
    // dueAmount is already mode-aware (the selected group's own amount in
    // separate mode, the whole invoice's total otherwise), so this one
    // formula covers both — no separate invoice-level branch needed here.
    const status: PaymentStatus =
      newPaid + credited >= dueAmount - 0.005 ? 'paid'
      : newPaid > 0 ? 'partial'
      : 'unpaid';

    const account = selectedAccount!;
    const groupId = selectedGroup?.id;

    try {
      const nowISO = new Date().toISOString();
      const payRef = doc(collection(db, 'invoices', inv.id, 'payments'));
      const invRef = doc(db, 'invoices', inv.id);
      const accRef = doc(db, 'bankAccounts', account.id);
      const txRef  = doc(collection(db, 'bankTransactions'));

      await runTransaction(db, async tx => {
        const accSnap = await tx.get(accRef);
        if (!accSnap.exists()) throw new Error('Bank account no longer exists.');
        // Separate mode: re-read the invoice fresh inside the transaction —
        // its dueGroups array is read-modify-written whole (Firestore can't
        // patch one array element in place), so it must reflect whatever's
        // actually there right now, not the possibly-stale client copy.
        const invSnap = isSeparate ? await tx.get(invRef) : null;

        let invoiceUpdate: Record<string, unknown>;
        if (isSeparate) {
          if (!invSnap!.exists()) throw new Error('That invoice no longer exists.');
          const freshGroups = ((invSnap!.data() as PurchaseInvoice).dueGroups ?? []) as DueGroup[];
          const idx = freshGroups.findIndex(g => g.id === groupId);
          if (idx === -1) throw new Error('That due date no longer exists on this invoice — please reload and try again.');
          const g = freshGroups[idx];
          const freshOutstanding = dueGroupOutstanding(g);
          if (amt > freshOutstanding + 0.005) {
            throw new Error(`Payment amount exceeds this due date's outstanding balance of ${fmtMoney(freshOutstanding, currency)} — it may have just been paid elsewhere. Please reload and try again.`);
          }
          const updatedGroup: DueGroup = { ...g, paidUSD: round2(g.paidUSD + amt) };
          updatedGroup.paymentStatus = dueGroupStatus(updatedGroup);
          const newGroups = [...freshGroups];
          newGroups[idx] = updatedGroup;
          const agg = aggregateFromDueGroups(newGroups);
          invoiceUpdate = {
            dueGroups: newGroups,
            paidUSD: agg.paidUSD, creditedUSD: agg.creditedUSD,
            paymentStatus: agg.paymentStatus, dueDate: agg.dueDate,
            updatedAt: nowISO,
          };
        } else {
          invoiceUpdate = { paidUSD: newPaid, paymentStatus: status, updatedAt: nowISO };
        }

        logActivity(tx, db, business!.id, user, {
          module: 'payment', action: 'create', recordId: inv.id, recordNo: inv.invoiceNo, partyId: inv.fromEntityId,
          amountUSD: amt, note: [account.name, form.referenceNo.trim() ? `Ref ${form.referenceNo.trim()}` : ''].filter(Boolean).join(' · '),
        });
        tx.set(payRef, stripUndef({
          invoiceId:          inv.id,
          businessId:         business!.id,
          amount:             amt,
          currency:           baseCurrency,
          bankAmount:         needsConversion ? bankAmount : undefined,
          bankCurrency:       needsConversion ? account.currency : undefined,
          exchangeRate:       needsConversion ? bankAmount / amt : undefined,
          paidDate:           form.paidDate,
          bankAccountId:      account.id,
          bankAccountName:    account.name,
          bankTransactionId:  txRef.id,   // link back so edits/deletes can find it
          dueGroupId:         groupId,
          referenceNo:        form.referenceNo.trim() || undefined,
          notes:              form.notes.trim()        || undefined,
          createdAt:          nowISO,
          createdBy:       user!.id,
        }));

        tx.update(invRef, invoiceUpdate);

        tx.set(txRef, stripUndef({
          businessId:    business!.id,
          accountId:     account.id,
          accountName:   account.name,
          currency:      account.currency,
          direction:     'out',
          amount:        bankAmount,
          date:          form.paidDate,
          description:   `${entityName || 'Payment'} — (${lotNoDisplay(inv.invoiceNo)})`,
          referenceType: 'ap_payment',
          referenceId:   inv.id,
          referenceNo:   form.referenceNo.trim() || undefined,
          notes:         form.notes.trim()        || undefined,
          createdAt:     nowISO,
          createdBy:     user!.id,
        }));

        const currentBal = (accSnap.data() as BankAccount).currentBalance;
        tx.update(accRef, { currentBalance: currentBal - bankAmount });

        // Ledger (additive — the scalars above stay the source of truth for
        // the current UI). Dr Accounts Payable / Cr the bank account — kept
        // in the business's own base currency (see RecordPaymentModal's
        // currency-conversion plan: no FX gain/loss account exists anywhere
        // in this ledger, so the GL entry itself is never converted — only
        // the bank account's own balance/transaction record is).
        postEntry(tx, db, buildEntry({
          businessId: business!.id,
          sourceType: 'ap_payment',
          sourceId: payRef.id,
          sourceCollection: `invoices/${inv.id}/payments`,
          date: form.paidDate,
          currency: baseCurrency,
          createdBy: user!.id,
          note: `Payment — ${entityName || 'supplier'} (${lotNoDisplay(inv.invoiceNo)})`,
          lines: linesApPayment(amt, inv.fromEntityId, account.id),
        }), entryId('ap_payment', payRef.id));
      });

      setForm(EMPTY);
      onSaved?.(amt);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record payment.');
    }
    setSaving(false);
  }

  function handleClose() { setForm(EMPTY); setError(''); onClose(); }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      open
      onClose={handleClose}
      hideHead
      size="lg"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={isOverpaying || (isSeparate && !selectedGroup)}>Record Payment</Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={entityName || 'Record Payment'}
        eyebrow="Record Payment"
        right={
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
              Lot No
            </div>
            <LotPill>{lotNoDisplay(invoice.invoiceNo)}</LotPill>
          </div>
        }
        onClose={handleClose}
      />

      {/* ── Two-column desktop layout ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, alignItems: 'start' }}>

        {/* ── LEFT: form ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Which due date — only for a dueDateMode: 'separate' invoice.
              Same selectable-card pattern as the bank-account picker below —
              payment tracking follows the due-date grouping, not the whole
              invoice, so this must be chosen before an amount makes sense. */}
          {isSeparate && (
            <div>
              <div className="form-section-label" style={{ marginBottom: 10 }}>Which Due Date?</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {groups.map(g => {
                  const isSelected = selectedGroupId === g.id;
                  const gOutstanding = dueGroupOutstanding(g);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setSelectedGroupId(g.id)}
                      disabled={gOutstanding <= 0.005}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', borderRadius: 'var(--r-md)',
                        border: isSelected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                        background: isSelected ? 'var(--primary-light)' : 'var(--surface)',
                        cursor: gOutstanding <= 0.005 ? 'not-allowed' : 'pointer', textAlign: 'left', width: '100%',
                        opacity: gOutstanding <= 0.005 ? 0.55 : 1,
                        transition: 'border-color 0.14s, background 0.14s',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)' }}>
                          {new Date(g.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {gOutstanding <= 0.005 ? 'Fully paid' : `${fmtMoney(g.paidUSD, currency)} paid of ${fmtMoney(g.amountUSD, currency)}`}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, fontFamily: 'var(--font-num)', fontSize: 13, color: gOutstanding > 0 ? 'var(--danger)' : 'var(--text-3)' }}>
                          {fmtMoney(gOutstanding, currency)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)' }}>outstanding</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pay From */}
          <div>
            <div className="form-section-label" style={{ marginBottom: 10 }}>Pay From</div>
            {accounts.length === 0 ? (
              <div className="msg msg-info" style={{ margin: 0, fontSize: 12 }}>
                No bank accounts found. Add one in Bank → Accounts first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {accounts.map(acc => {
                  const isSelected = form.bankAccountId === acc.id;
                  return (
                    <button
                      key={acc.id}
                      type="button"
                      onClick={() => set('bankAccountId', acc.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '11px 14px', borderRadius: 'var(--r-md)',
                        border: isSelected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                        background: isSelected ? 'var(--primary-light)' : 'var(--surface)',
                        cursor: 'pointer', textAlign: 'left', width: '100%',
                        transition: 'border-color 0.14s, background 0.14s',
                      }}
                    >
                      {/* Account icon */}
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: acc.type === 'cash'
                          ? (isSelected ? 'color-mix(in srgb, var(--success) 22%, var(--success-bg))' : 'var(--success-bg)')
                          : (isSelected ? 'color-mix(in srgb, var(--primary) 22%, var(--primary-light))' : 'var(--primary-light)'),
                        color: acc.type === 'cash' ? 'var(--success)' : 'var(--primary)',
                      }}>
                        {acc.type === 'cash' ? <IconCash /> : <IconBank />}
                      </div>

                      {/* Name + detail */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)', marginBottom: 2 }}>
                          {acc.name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {acc.type === 'bank' && acc.bankName ? acc.bankName : 'Cash'}
                          {acc.accountNumber ? ` · ****${acc.accountNumber.slice(-4)}` : ''}
                          {' · '}{acc.currency}
                        </div>
                      </div>

                      {/* Balance + check */}
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, fontFamily: 'var(--font-num)', fontSize: 13, color: 'var(--text-1)' }}>
                          {fmtMoney(acc.currentBalance, acc.currency)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)' }}>available</div>
                      </div>

                      {/* Check indicator */}
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isSelected ? 'var(--primary)' : 'var(--surface-2)',
                        border: isSelected ? 'none' : '1.5px solid var(--border)',
                        color: '#fff',
                        transition: 'background 0.14s',
                      }}>
                        {isSelected && <IconCheck />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Payment details */}
          <div>
            <div className="form-section-label" style={{ marginBottom: 10 }}>Payment Details</div>

            <div className="form-row-2">
              <FormField label={`Amount (${currency})`} required>
                <div style={{ position: 'relative' }}>
                  <div className="input-wrap has-prefix" style={isOverpaying ? { borderColor: 'var(--danger)' } : undefined}>
                    <span className="input-prefix">{sym}</span>
                    <input
                      className="no-spinner"
                      type="number" step="0.01" min="0.01"
                      value={form.amount}
                      onChange={e => set('amount', e.target.value)}
                      placeholder={outstanding.toFixed(2)}
                      autoFocus
                    />
                  </div>
                  {outstanding > 0 && (
                    <button
                      type="button"
                      onClick={() => set('amount', outstanding.toFixed(2))}
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        background: 'var(--primary-light)', color: 'var(--primary)',
                        border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 700,
                        padding: '2px 7px', cursor: 'pointer', letterSpacing: '0.02em',
                      }}
                    >
                      FULL
                    </button>
                  )}
                </div>
                {isOverpaying && (
                  <p className="form-hint" style={{ color: 'var(--danger)', fontWeight: 600, marginTop: 4 }}>
                    Payment amount exceeds the outstanding balance of {fmtMoney(outstanding, currency)}. Please adjust the amount before proceeding.
                  </p>
                )}
              </FormField>
              <FormField label="Payment Date" required>
                <input type="date" value={form.paidDate} onChange={e => set('paidDate', e.target.value)} />
              </FormField>
            </div>

            {needsConversion && (
              <div className="form-row-2">
                <FormField
                  label={`Conversion Rate (1 ${baseCurrency} = ? ${selectedAccount!.currency})`}
                  required
                  hint={configuredRate
                    ? `Pre-filled from Settings › Dashboard's Exchange Rates card — adjust if the bank's actual rate today differs.`
                    : `No exchange rate configured for ${baseCurrency} → ${selectedAccount!.currency} — enter today's rate (or set one on the Dashboard's Exchange Rates card to auto-fill this next time).`}
                >
                  <input
                    className="no-spinner"
                    type="number" step="0.0001" min="0.0001"
                    value={form.rate}
                    onChange={e => { set('rate', e.target.value); setRateTouched(true); }}
                    placeholder="0.0000"
                  />
                </FormField>
                <FormField label={`Converted Amount (${selectedAccount!.currency})`}>
                  <div className="input-wrap has-prefix">
                    <span className="input-prefix">{bankSym}</span>
                    <input value={bankAmt > 0 ? bankAmt.toFixed(2) : ''} placeholder="0.00" disabled />
                  </div>
                </FormField>
              </div>
            )}

            <FormField label="Reference No." hint="Bank ref, wire ref, cheque number, etc.">
              <input
                value={form.referenceNo}
                onChange={e => set('referenceNo', e.target.value)}
                placeholder="Optional"
                style={{ fontFamily: 'var(--font-num)' }}
              />
            </FormField>

            <FormField label="Notes (optional)">
              <textarea
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                placeholder="Any notes about this payment…"
                style={{ minHeight: 60, resize: 'vertical' }}
              />
            </FormField>
          </div>
        </div>

        {/* ── RIGHT: summary panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Invoice breakdown */}
          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)', overflow: 'hidden',
          }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-2)', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {isSeparate ? 'Due Date Summary' : 'Invoice Summary'}
            </div>
            {[
              { label: isSeparate ? 'Due Amount' : 'Invoice Total', val: dueAmount, color: 'var(--text-1)' },
              { label: 'Already Paid',  val: alreadyPaid,            color: 'var(--success)' },
              { label: 'Outstanding',   val: outstanding,            color: outstanding > 0 ? 'var(--danger)' : 'var(--text-3)' },
            ].map(({ label, val, color }, i, arr) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px',
                borderBottom: i < arr.length - 1 ? '1px solid var(--border-2)' : 'none',
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{label}</span>
                <span style={{ fontFamily: 'var(--font-num)', fontSize: 13, fontWeight: 700, color }}>
                  {sym + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>

          {/* After-payment preview — only shown when amount is entered */}
          {payAmt > 0 && (
            <div style={{
              background: previewStatus === 'paid' ? 'var(--success-bg)' : 'var(--surface-2)',
              border: `1px solid ${previewStatus === 'paid' ? 'color-mix(in srgb, var(--success) 35%, transparent)' : 'var(--border)'}`,
              borderRadius: 'var(--r-md)', overflow: 'hidden',
            }}>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${previewStatus === 'paid' ? 'color-mix(in srgb, var(--success) 35%, transparent)' : 'var(--border-2)'}`, fontSize: 11, fontWeight: 700, color: previewStatus === 'paid' ? 'var(--success)' : 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                After Payment
              </div>

              <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>You pay</span>
                  <span style={{ fontFamily: 'var(--font-num)', fontSize: 14, fontWeight: 800, color: 'var(--danger)' }}>
                    −{sym}{payAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Still owed</span>
                  <span style={{ fontFamily: 'var(--font-num)', fontSize: 13, fontWeight: 700, color: remaining > 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {sym + remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Invoice status</span>
                  <span className={`badge badge-${previewStatus === 'paid' ? 'active' : previewStatus === 'partial' ? 'warning' : 'draft'}`} style={{ fontSize: 11 }}>
                    {previewStatus === 'paid' ? '✓ Paid' : previewStatus === 'partial' ? 'Partial' : 'Unpaid'}
                  </span>
                </div>
                {afterBalance !== null && (
                  <div style={{
                    marginTop: 4, paddingTop: 8, borderTop: `1px solid ${previewStatus === 'paid' ? 'color-mix(in srgb, var(--success) 35%, transparent)' : 'var(--border-2)'}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{selectedAccount?.name} balance</span>
                    <span style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: afterBalance < 0 ? 'var(--danger)' : 'var(--text-2)' }}>
                      {fmtMoney(afterBalance, selectedAccount?.currency ?? currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Placeholder when no amount typed */}
          {payAmt === 0 && (
            <div style={{
              border: '1.5px dashed var(--border)', borderRadius: 'var(--r-md)',
              padding: '18px 14px', textAlign: 'center',
              color: 'var(--text-3)', fontSize: 12,
            }}>
              Enter an amount to see payment preview
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
